import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link, useLocation, useNavigationType } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal from "@/components/Reveal";
import InsightCard from "@/components/InsightCard";
import ReadingSeries from "@/components/ReadingSeries";
import { articles } from "@/data/siteData";
import { normalizeArticle } from "@/data/normalizeArticle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSIGHTS_FILTER_KEY = "insights-selected-filter";
const INSIGHTS_SCROLL_KEY = "insights-scroll-position";
const slug = (value) => value.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");

function preferEditorialSample(article) {
  const local = articles.find((item) => item.slug === article.slug);
  return local || normalizeArticle(article);
}

export default function Insights() {
  const navigationType = useNavigationType();
  const location = useLocation();
  const [catalogue, setCatalogue] = useState(articles);
  const [source, setSource] = useState("database");
  const [selected, setSelected] = useState(() => sessionStorage.getItem(INSIGHTS_FILTER_KEY) || "All");

  useEffect(() => {
    api.get(`${API}/articles`).then(({ data }) => setCatalogue(data.map(preferEditorialSample))).catch(() => setSource("sample"));
  }, []);

  useEffect(() => {
    sessionStorage.setItem(INSIGHTS_FILTER_KEY, selected);
  }, [selected]);

  useEffect(() => {
    const saveScrollPosition = () => sessionStorage.setItem(INSIGHTS_SCROLL_KEY, String(window.scrollY));
    const savedScrollPosition = Number(sessionStorage.getItem(INSIGHTS_SCROLL_KEY) || 0);
    const shouldRestore = navigationType === "POP" || location.state?.restoreInsights;
    if (!shouldRestore || !savedScrollPosition) saveScrollPosition();
    window.addEventListener("scroll", saveScrollPosition, { passive: true });
    return () => window.removeEventListener("scroll", saveScrollPosition);
  }, [location.key, location.state, navigationType]);

  useEffect(() => {
    const savedScrollPosition = Number(sessionStorage.getItem(INSIGHTS_SCROLL_KEY) || 0);
    const shouldRestore = navigationType === "POP" || location.state?.restoreInsights;
    if (!shouldRestore || !savedScrollPosition) return undefined;

    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => window.scrollTo({ top: savedScrollPosition, left: 0, behavior: "auto" }));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [location.key, location.state, navigationType]);

  const categories = useMemo(() => ["All", ...new Set(catalogue.map((article) => article.category))], [catalogue]);
  const visible = selected === "All" ? catalogue : catalogue.filter((article) => article.category === selected);
  const featured = selected === "All" ? catalogue[0] : null;
  const cards = featured ? visible.slice(1) : visible;

  return (
    <main data-testid="insights-page">
      <PageIntro index="03" title="Insights" description="Notes from inside the work: data and analytics modernization, enterprise architecture, semantic foundations, AI governance, and the questions that become Decision Intelligence." aside="I write about the moments when a technical choice meets a human responsibility. Drafts remain private until I am ready to put them into the world." />
      <section className="page-section" style={{ paddingTop: 0 }}>
        <div className="shell">
          <div className="articles-discovery-bridge">
            <div><span className="section-kicker">Published elsewhere</span><p>Looking for external long-form essays and publication series? Explore Articles on Medium and beyond.</p></div>
            <Link to="/articles" className="inline-link" data-testid="insights-articles-discovery-link">Explore Articles <ArrowUpRight size={15} /></Link>
          </div>
          <Reveal>
            <div className="filter-bar" data-testid="insights-filter-bar">
              {categories.map((category) => <button key={category} type="button" className={`filter-button ${selected === category ? "active" : ""}`} data-testid={`insights-filter-${slug(category)}`} onClick={() => setSelected(category)}>{category}</button>)}
            </div>
          </Reveal>
          {selected === "All" && <ReadingSeries />}
          {featured && <Reveal><div className="insights-feature" data-testid="featured-insight"><div className="insights-feature-label"><span className="feature-code">A note to begin / 001</span><span className="feature-word">The<br /><em>decision</em><br />gap.</span></div><div className="insights-feature-content"><div className="meta-row"><span className="insight-category">{featured.category}</span><span>{featured.date} · {featured.readingTime}</span></div><h2>{featured.title}</h2><p>{featured.excerpt}</p><Link to={`/insights/${featured.slug}`} className="button-dark" data-testid="featured-insight-link">Read the note <ArrowUpRight size={15} /></Link></div></div></Reveal>}
          <div className="insight-grid" data-testid="insight-catalogue">
            {cards.map((article, index) => <Reveal key={article.slug} delay={index * 70}><InsightCard article={article} /></Reveal>)}
          </div>
          <p className="catalogue-note" data-testid="insights-source-note">{source === "database" ? "This notebook is connected to the private editor. I will keep adding notes as the questions become clear and the wording is ready." : "The opening sample notebook is being shown while the editorial connection is unavailable."} <Link to="/insights/manage" data-testid="insights-editor-entry-link">Open the owner editor <ArrowUpRight size={13} /></Link></p>
        </div>
      </section>
    </main>
  );
}
